import { Template } from "@/types";
import { Eye } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

interface TemplateCardProps {
  template: Template;
  selected: boolean;
  onClick: () => void;
  onPreviewClick?: (template: Template) => void;
}

const TemplateCard = ({ template, selected, onClick, onPreviewClick }: TemplateCardProps) => {
  const isSystemTemplate = template.isSystem || template.is_system;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreviewClick?.(template);
  };

  return (
    <div
      className={`
        relative p-4 rounded-lg border transition-all duration-300 cursor-pointer flex flex-col h-full
        ${selected
          ? "border-brand-500 ring-2 ring-brand-500/20 bg-brand-50 dark:bg-brand-950/40 shadow-lg transform scale-[1.02]"
          : "border-border hover:border-brand-300 hover:shadow-md hover:scale-[1.01]"
        }
        ${isMobile ? 'active:scale-[0.98]' : ''}
      `}
      onClick={onClick}
    >
      {/* Radio button для индикации выбора */}
      <div className="absolute top-4 left-4 z-10">
        <div className={`
          w-5 h-5 rounded-full border-2 transition-all duration-300 flex items-center justify-center
          ${selected
            ? "border-brand-500 bg-brand-500"
            : "border-gray-300 hover:border-brand-400"
          }
        `}>
          {selected && (
            <div className="w-2 h-2 rounded-full bg-white animate-in zoom-in-50 duration-200" />
          )}
        </div>
      </div>

      {/* Основной контент */}
      <div className="flex flex-col h-full pl-8">
        {/* Заголовок */}
        <div className="mb-3">
          <h3 className={`font-semibold ${isMobile ? "text-sm" : "text-base"} leading-tight mb-1`}>
            {template.name}
          </h3>
          {/* Тип шаблона под названием */}
          <div className="text-xs">
            {isSystemTemplate ? (
              <span className="text-muted-foreground">Системный шаблон</span>
            ) : (
              <span className="text-brand-600 dark:text-brand-400">Пользовательский шаблон</span>
            )}
          </div>
        </div>

        {/* Описание шаблона */}
        {template.description && (
          <p className={`text-muted-foreground mb-4 flex-grow ${isMobile ? "text-xs leading-relaxed" : "text-sm leading-relaxed"} line-clamp-3`}>
            {template.description}
          </p>
        )}

        {/* Нижняя панель с кнопкой предпросмотра */}
        {onPreviewClick && (
          <div className="pt-3 border-t border-border/50 mt-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePreviewClick}
              className="w-full h-9 text-xs hover:bg-brand-100 dark:hover:bg-brand-900/20 transition-colors flex items-center justify-center gap-1.5"
            >
              <Eye className="h-3.5 w-3.5" />
              Предпросмотр
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateCard;
